import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS as CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';

const AUTO_CLOSE_SECONDS = 5;

export interface EnquiryGateSuccessProps {
    /**
     * Which success screen to show (design frames 4792:39750 / 4502:31068):
     * `existing` confirms and auto-closes; `new` welcomes the fresh account
     * and leads into onboarding.
     */
    variant: 'existing' | 'new';
    /** The professional the enquiry was sent to. */
    professionalTitle: string;
    /**
     * The conversation the enquiry created, if one came back. When present a
     * "View conversation" inline link deep-links into the inbox (new-user
     * screen only — the existing-user screen auto-closes).
     */
    conversationLink?: { to: string; isNonRemixRoute: boolean } | null;
    /**
     * The account was created by the submit rather than by the reader, so they
     * do not know they have one. Only true for the silent auto-create path —
     * someone who just signed up in the modal knows perfectly well.
     */
    accountAutoCreated?: boolean;
    /** Close the modal (the parent reloads the page when auth happened in-modal). */
    onClose: () => void;
    /** New users: continue into the normal onboarding flow. */
    onExplore: () => void;
}

/** Existing-account confirmation: short, then closes itself. */
const ExistingUserSuccess = ({
    professionalTitle,
    onClose,
}: Pick<EnquiryGateSuccessProps, 'professionalTitle' | 'onClose'>) => {
    const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

    useEffect(() => {
        if (secondsLeft <= 0) {
            onClose();
            return;
        }
        const timer = setTimeout(() => {
            setSecondsLeft((s) => s - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft, onClose]);

    return (
        <div className="mx-auto flex min-h-0 w-full max-w-86 flex-1 flex-col">
            {/* Two elements, one voice: the design (4792:39750) sets the label
                and the sentence at the same size, so they read as one statement
                — but as separate nodes, so the check sits against "Success"
                rather than stranded mid-paragraph, and the two hold their own
                gap instead of running together on a surface that hugs. */}
            <h2 className="mt-9 flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Success
                <Check className="size-6 shrink-0" strokeWidth={2} />
            </h2>
            <p className="mt-3 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Your enquiry has been sent successfully.
            </p>
            {/* Spreads the card, which has a fixed height to spread. The sheet
                hugs its content so this collapses to nothing there — hence the
                margin below, which is the floor on both. */}
            <div className="flex-1" />
            <p className="mt-8 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
                You&rsquo;ll receive a notification here and via email when{' '}
                {professionalTitle} responds.
            </p>
            <GateStickyFooter className="mt-5" desktopClassName="mb-2">
                <button type="button" onClick={onClose} className={CTA_CLASS}>
                    Close
                </button>
                <p className="mt-4 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]">
                    Closing in {secondsLeft}...
                </p>
            </GateStickyFooter>
        </div>
    );
};

/** Fresh-account confirmation: welcomes the member and leads into onboarding. */
const NewUserSuccess = ({
    professionalTitle,
    conversationLink,
    accountAutoCreated,
    onClose,
    onExplore,
}: Omit<EnquiryGateSuccessProps, 'variant'>) => (
    <div className="mx-auto my-auto flex w-full max-w-86 flex-col">
        <h2 className="flex items-center gap-2.5 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
            Enquiry sent
            <Check className="size-6 shrink-0" strokeWidth={2} />
        </h2>
        <p className="mt-5 text-[1.1875rem] leading-snug font-medium tracking-[-0.02em] text-[#181818]">
            Your message has been delivered successfully to {professionalTitle}.
            We&rsquo;ll notify you as soon as you receive a reply.
        </p>
        {/* Where the Secure Sign-In offer belongs once BC-4831 lands: an
            auto-created account is passwordless, so a passkey is not an extra —
            it is how the reader gets back in at all. It takes the primary
            action here and Explore steps down to secondary. */}
        <p className="mt-4 text-[1.1875rem] leading-snug tracking-[-0.02em] text-[#828282]">
            {accountAutoCreated
                ? 'We’ve set up your ArchiPro account so you can track it — no password needed.'
                : 'In the meantime, you can continue planning your project with your saved products, design boards, and project tools.'}{' '}
            {conversationLink && (
                <>
                    {' '}
                    {/* Plain anchor on purpose: a full load closes the modal
                        and resets trackers when auth happened in-modal —
                        client-side nav would soft-navigate under the open
                        dialog. */}
                    <a
                        href={conversationLink.to}
                        className="text-[#181818] underline"
                    >
                        View conversation
                    </a>
                </>
            )}
        </p>
        <GateStickyFooter className="mt-8">
            <button type="button" onClick={onExplore} className={CTA_CLASS}>
                Explore my ArchiPro
            </button>
            <button
                type="button"
                onClick={onClose}
                className="mt-4 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
            >
                Close
            </button>
        </GateStickyFooter>
    </div>
);

/**
 * Success step of the enquiry gate (BC-4833). Existing accounts get a short
 * confirmation that closes itself after {@link AUTO_CLOSE_SECONDS}; newly
 * registered accounts (in-modal signup or AutoCreateMember) get a welcome
 * screen whose primary CTA continues the normal onboarding flow.
 */
export const EnquiryGateSuccess = (props: EnquiryGateSuccessProps) =>
    props.variant === 'existing' ? (
        <ExistingUserSuccess
            professionalTitle={props.professionalTitle}
            onClose={props.onClose}
        />
    ) : (
        <NewUserSuccess {...props} />
    );
